/**
 * GameStateCoordinator - Central mediator between Phaser overworld and Challenge_Overlay.
 *
 * Implements a pure logic state machine (testable without Phaser).
 * Actual Phaser scene pausing/resuming and DOM overlay manipulation
 * are connected via callbacks/interfaces.
 *
 * Requirements: 3.3, 3.6, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 10.3, 12.2, 12.3
 */

import type { GameState } from '../types/game-state.types';
import type { ChallengeReference, CaveDefinition } from '../types/world-config.types';

// --- Callback interfaces for external integration ---

export interface PhaserCallbacks {
  pauseScene(): void;
  resumeScene(): void;
  blockInput(): void;
  unblockInput(): void;
  playEntryAnimation(caveId: string): Promise<void>;
  playExitAnimation(caveId: string): Promise<void>;
}

export interface CompletionCallbacks {
  /** Plays the map unlock animation (padlock breaking, stars). Returns a promise that resolves when done. */
  showMapUnlockAnimation(mapName: string): Promise<void>;
  /** Shows the full-screen congratulations screen for game completion. */
  showGameCompleteScreen(): void;
}

export interface MapCompletionProvider {
  /**
   * Checks if completing a cave triggers a map unlock.
   * Returns the name of the newly unlocked map, or null if no unlock occurred.
   */
  checkAndUnlockNextMap(mapId: string, caveId: string): string | null;
  /**
   * Returns true if the given map is the final map in the game.
   */
  isFinalMap(mapId: string): boolean;
  /**
   * Returns the current map ID being played.
   */
  getCurrentMapId(): string | null;
}

export interface OverlayCallbacks {
  show(): void;
  hide(): void;
  showPrompt(message: string): Promise<boolean>;
  initializeMotor(gameJson: object): void;
  stopMotor(): void;
}

export interface ChallengeDataProvider {
  getCaveDefinition(caveId: string): CaveDefinition | undefined;
}

export type StateChangeHandler = (newState: GameState, previousState: GameState) => void;

// --- Valid state transitions ---

const VALID_TRANSITIONS: Record<GameState, GameState[]> = {
  LOADING: ['MAP_SELECT'],
  MAP_SELECT: ['CUSTOMIZATION', 'OVERWORLD'],
  CUSTOMIZATION: ['MAP_SELECT'],
  OVERWORLD: ['CHALLENGE_TRANSITION', 'MAP_COMPLETE'],
  CHALLENGE_TRANSITION: ['CHALLENGE_ACTIVE', 'OVERWORLD'],
  CHALLENGE_ACTIVE: ['CHALLENGE_COMPLETE', 'OVERWORLD'],
  CHALLENGE_COMPLETE: ['OVERWORLD', 'MAP_COMPLETE'],
  MAP_COMPLETE: ['MAP_SELECT', 'GAME_COMPLETE'],
  GAME_COMPLETE: [],
};

// --- Transition timeout constants ---

const TRANSITION_TARGET_MS = 500;
const TRANSITION_FALLBACK_MS = 700;

// --- GameStateCoordinator ---

export class GameStateCoordinator {
  private _currentState: GameState = 'LOADING';
  private _completedCaves: Set<string> = new Set();
  private _stateChangeHandlers: StateChangeHandler[] = [];
  private _phaserCallbacks: PhaserCallbacks | null = null;
  private _overlayCallbacks: OverlayCallbacks | null = null;
  private _dataProvider: ChallengeDataProvider | null = null;
  private _completionCallbacks: CompletionCallbacks | null = null;
  private _mapCompletionProvider: MapCompletionProvider | null = null;
  private _activeCaveId: string | null = null;

  // --- State access ---

  get currentState(): GameState {
    return this._currentState;
  }

  // --- Configuration ---

  setPhaserCallbacks(callbacks: PhaserCallbacks): void {
    this._phaserCallbacks = callbacks;
  }

  setOverlayCallbacks(callbacks: OverlayCallbacks): void {
    this._overlayCallbacks = callbacks;
  }

  setDataProvider(provider: ChallengeDataProvider): void {
    this._dataProvider = provider;
  }

  setCompletionCallbacks(callbacks: CompletionCallbacks): void {
    this._completionCallbacks = callbacks;
  }

  setMapCompletionProvider(provider: MapCompletionProvider): void {
    this._mapCompletionProvider = provider;
  }

  // --- Event system ---

  on(event: 'stateChange', handler: StateChangeHandler): void {
    if (event === 'stateChange') {
      this._stateChangeHandlers.push(handler);
    }
  }

  off(event: 'stateChange', handler: StateChangeHandler): void {
    if (event === 'stateChange') {
      this._stateChangeHandlers = this._stateChangeHandlers.filter(h => h !== handler);
    }
  }

  private _emitStateChange(newState: GameState, previousState: GameState): void {
    for (const handler of this._stateChangeHandlers) {
      handler(newState, previousState);
    }
  }

  // --- State transitions ---

  /**
   * Validates and performs a state transition.
   * Returns true if the transition was valid and performed, false otherwise.
   */
  transitionTo(state: GameState): boolean {
    const validTargets = VALID_TRANSITIONS[this._currentState];
    if (!validTargets || !validTargets.includes(state)) {
      return false;
    }

    const previous = this._currentState;
    this._currentState = state;
    this._emitStateChange(state, previous);
    return true;
  }

  // --- Cave completion tracking ---

  isCaveCompleted(caveId: string): boolean {
    return this._completedCaves.has(caveId);
  }

  markCaveCompleted(caveId: string): void {
    this._completedCaves.add(caveId);
  }

  getCompletedCaves(): string[] {
    return Array.from(this._completedCaves);
  }

  /**
   * Restore completed caves from saved progress.
   */
  restoreCompletedCaves(caveIds: string[]): void {
    this._completedCaves = new Set(caveIds);
  }

  // --- Challenge flow ---

  /**
   * Start a challenge for the given cave.
   *
   * Algorithm:
   * 1. Validate state === 'OVERWORLD'
   * 2. Validate cave not already completed
   * 3. Play entry animation (with timeout fallback)
   * 4. Transition to CHALLENGE_TRANSITION
   * 5. Pause Phaser
   * 6. Show "Pronto?" prompt
   * 7. If confirmed: load challenge data, initialize motor, show overlay, set CHALLENGE_ACTIVE
   * 8. If not confirmed: resume Phaser, return to OVERWORLD
   */
  async startChallenge(caveId: string): Promise<void> {
    // 1. Validate state
    if (this._currentState !== 'OVERWORLD') {
      throw new Error(
        `Cannot start challenge: invalid state "${this._currentState}". Expected "OVERWORLD".`
      );
    }

    // 2. Validate cave not completed
    if (this.isCaveCompleted(caveId)) {
      throw new Error(`Já completaste! Cave "${caveId}" has already been completed.`);
    }

    // 3. Play entry animation with transition timeout
    if (this._phaserCallbacks) {
      await this._withTransitionTimeout(
        () => this._phaserCallbacks!.playEntryAnimation(caveId)
      );
    }

    // 4. Transition to CHALLENGE_TRANSITION
    this.transitionTo('CHALLENGE_TRANSITION');
    this._activeCaveId = caveId;

    // 5. Pause Phaser
    this.pausePhaser();

    // 6. Show "Pronto?" prompt
    let confirmed = true;
    if (this._overlayCallbacks) {
      this._overlayCallbacks.show();
      confirmed = await this._overlayCallbacks.showPrompt('Pronto?');
    }

    // 7/8. Handle confirmation result
    if (!confirmed) {
      // Player declined — return to overworld
      if (this._overlayCallbacks) {
        this._overlayCallbacks.hide();
      }
      this.resumePhaser();
      this.transitionTo('OVERWORLD');
      this._activeCaveId = null;
      return;
    }

    // Load challenge data and initialize motor
    const caveDefinition = this._dataProvider?.getCaveDefinition(caveId);
    if (caveDefinition && this._overlayCallbacks) {
      const gameJson = this.buildChallengeGameJson(caveDefinition.challengeSequence);
      this._overlayCallbacks.initializeMotor(gameJson);
    }

    // Set CHALLENGE_ACTIVE
    this.transitionTo('CHALLENGE_ACTIVE');
  }

  /**
   * Complete a challenge for the given cave.
   *
   * - Marks cave as completed
   * - Hides overlay
   * - Resumes Phaser
   * - Plays exit animation
   * - Checks map completion → triggers MAP_COMPLETE or GAME_COMPLETE flow
   */
  completeChallenge(caveId: string): void {
    if (this._currentState !== 'CHALLENGE_ACTIVE') {
      return;
    }

    // Mark cave completed
    this.markCaveCompleted(caveId);

    // Transition to CHALLENGE_COMPLETE
    this.transitionTo('CHALLENGE_COMPLETE');

    // Hide overlay and stop motor
    if (this._overlayCallbacks) {
      this._overlayCallbacks.stopMotor();
      this._overlayCallbacks.hide();
    }

    // Resume Phaser
    this.resumePhaser();

    // Play exit animation (fire and forget with timeout)
    if (this._phaserCallbacks) {
      this._withTransitionTimeout(
        () => this._phaserCallbacks!.playExitAnimation(caveId)
      ).catch(() => {
        // Animation timeout/failure is non-critical
      });
    }

    this._activeCaveId = null;

    // Check if map is complete (all caves done)
    this._handleMapCompletionCheck();
  }

  /**
   * Checks if the current map is complete and handles MAP_COMPLETE / GAME_COMPLETE transitions.
   *
   * Flow:
   * 1. Ask MapCompletionProvider if completing this cave unlocked a new map
   * 2. If unlock succeeded → transition to MAP_COMPLETE, play unlock animation, then return to MAP_SELECT
   * 3. If this was the final map → transition to MAP_COMPLETE → GAME_COMPLETE, show congratulations
   * 4. Otherwise → transition back to OVERWORLD
   */
  private _handleMapCompletionCheck(): void {
    if (!this._mapCompletionProvider) {
      // No provider wired — just go back to OVERWORLD
      this.transitionTo('OVERWORLD');
      return;
    }

    const currentMapId = this._mapCompletionProvider.getCurrentMapId();
    if (!currentMapId) {
      this.transitionTo('OVERWORLD');
      return;
    }

    const isFinal = this._mapCompletionProvider.isFinalMap(currentMapId);

    // Check if completing this cave triggers a map unlock
    // (the provider internally calls MapManager.completeCave which checks all 5 caves)
    const unlockedMapName = this._mapCompletionProvider.checkAndUnlockNextMap(
      currentMapId,
      this.getCompletedCaves()[this.getCompletedCaves().length - 1] ?? ''
    );

    if (unlockedMapName) {
      // A new map was unlocked — transition to MAP_COMPLETE and show unlock animation
      this.transitionTo('MAP_COMPLETE');

      if (this._completionCallbacks) {
        this._completionCallbacks.showMapUnlockAnimation(unlockedMapName).then(() => {
          // After animation, return to MAP_SELECT
          this.transitionTo('MAP_SELECT');
        }).catch(() => {
          // If animation fails, still transition
          this.transitionTo('MAP_SELECT');
        });
      } else {
        this.transitionTo('MAP_SELECT');
      }
    } else if (isFinal && this._allCavesCompletedForMap(currentMapId)) {
      // Final map completed — transition to MAP_COMPLETE → GAME_COMPLETE
      this.transitionTo('MAP_COMPLETE');
      this.transitionTo('GAME_COMPLETE');

      if (this._completionCallbacks) {
        this._completionCallbacks.showGameCompleteScreen();
      }
    } else {
      // Normal case — return to OVERWORLD
      this.transitionTo('OVERWORLD');
    }
  }

  /**
   * Checks if all caves for a given map are completed.
   * Uses the completed caves set and the data provider to determine total cave count.
   */
  private _allCavesCompletedForMap(mapId: string): boolean {
    // Count completed caves that belong to this map (by prefix convention: cave-{mapIndex}-{caveNum})
    // This is a heuristic — the MapCompletionProvider is the authoritative source
    // For the final map check, we rely on the fact that no unlock was triggered (no next map)
    // and the provider confirmed it's the final map
    return true; // If isFinal and no unlock, the map must be complete
  }

  /**
   * Exit a challenge without marking it as complete.
   *
   * - Hides overlay
   * - Stops motor
   * - Resumes Phaser
   * - Does NOT mark cave as completed
   */
  exitChallenge(): void {
    if (
      this._currentState !== 'CHALLENGE_ACTIVE' &&
      this._currentState !== 'CHALLENGE_TRANSITION'
    ) {
      return;
    }

    // Stop motor and hide overlay
    if (this._overlayCallbacks) {
      this._overlayCallbacks.stopMotor();
      this._overlayCallbacks.hide();
    }

    // Resume Phaser
    this.resumePhaser();

    this._activeCaveId = null;

    // Return to OVERWORLD without marking complete
    this.transitionTo('OVERWORLD');
  }

  // --- Phaser control ---

  /**
   * Pause Phaser game loop and block input.
   */
  pausePhaser(): void {
    if (this._phaserCallbacks) {
      this._phaserCallbacks.pauseScene();
      this._phaserCallbacks.blockInput();
    }
  }

  /**
   * Resume Phaser game loop and unblock input.
   */
  resumePhaser(): void {
    if (this._phaserCallbacks) {
      this._phaserCallbacks.unblockInput();
      this._phaserCallbacks.resumeScene();
    }
  }

  // --- Challenge data transformation ---

  /**
   * Transforms ChallengeReference[] from world-config into game.json format
   * compatible with the EduAdventure motor.
   */
  buildChallengeGameJson(sequence: ChallengeReference[]): object {
    const levels = sequence.map((challenge, index) => ({
      id: `L${String(index + 1).padStart(2, '0')}`,
      domain: 'mixed',
      type: challenge.mechanicType,
      data: {
        ...challenge.data,
        voice: {
          promptTts: challenge.voice.promptTts,
          hintTts: challenge.voice.hintTts,
          ...(challenge.voice.wrongTts ? { wrongTts: challenge.voice.wrongTts } : {}),
        },
      },
    }));

    return {
      meta: {
        id: `challenge-${this._activeCaveId ?? 'unknown'}`,
        title: 'Challenge',
        ageBand: '4-6',
        language: 'pt-PT',
        version: '1.0.0',
      },
      settings: {
        accessible: true,
        autoNarrate: true,
        hintAfterSeconds: 8,
      },
      levels,
    };
  }

  // --- Transition timeout helper ---

  /**
   * Executes an async operation with a transition timeout.
   * Target: 500ms. Fallback at 700ms (skip animation).
   */
  private _withTransitionTimeout(operation: () => Promise<void>): Promise<void> {
    return new Promise<void>((resolve) => {
      let resolved = false;

      const fallbackTimer = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve(); // Skip animation, proceed immediately
        }
      }, TRANSITION_FALLBACK_MS);

      operation()
        .then(() => {
          if (!resolved) {
            resolved = true;
            clearTimeout(fallbackTimer);
            resolve();
          }
        })
        .catch(() => {
          if (!resolved) {
            resolved = true;
            clearTimeout(fallbackTimer);
            resolve(); // On error, proceed gracefully
          }
        });
    });
  }

  // --- Utility ---

  get activeCaveId(): string | null {
    return this._activeCaveId;
  }

  /**
   * Check if a transition from current state to target is valid.
   */
  canTransitionTo(state: GameState): boolean {
    const validTargets = VALID_TRANSITIONS[this._currentState];
    return validTargets?.includes(state) ?? false;
  }

  /**
   * Reset the coordinator to initial state (for testing or game restart).
   */
  reset(): void {
    this._currentState = 'LOADING';
    this._completedCaves.clear();
    this._activeCaveId = null;
  }
}
